/**
 * useCropPatternAtlasEffect — registers `crop-stripe-{id}` and `crop-dots-{id}`
 * pattern images required by `crop-fill-planned` / `crop-fill-harvested`
 * layers (registry.ts §11).
 *
 * AK-104 founder UAT 2026-05-08: V2 layer registry split crop-fill into
 * 3 phase sub-layers (planned hatched, growing solid, harvested dots) but
 * the actual pattern bitmaps must be registered with `map.addImage(id, data)`
 * before MapLibre can render them. This effect handles that side-effect once
 * the map is ready and reference.crops is fetched.
 *
 * Idempotent: `buildCropPatternAtlas` skips IDs already registered. Safe
 * across re-renders / StrictMode double-mount.
 *
 * Lifecycle:
 *   - mount → fetch /api/v1/reference/crops?limit=500
 *   - on map.isStyleLoaded() → buildCropPatternAtlas(map, crops)
 *   - if style not yet loaded, listen for map.on('load', …)
 */
import { useEffect } from 'react';
import type maplibregl from 'maplibre-gl';
import { buildCropPatternAtlas } from '../cropPatterns';
import type { Crop } from '../useCropsReference';
import { MAP_NO_DATA_GREY } from '@/lib/colorMaps/mapBrand';

interface CropApiRow {
  id: string;
  name?: string | null;
  name_translations?: Record<string, string> | null;
  color?: string | null;
  icon_url?: string | null;
}

interface CropsListResponse {
  items: CropApiRow[];
  total?: number;
}

export function useCropPatternAtlasEffect(map: maplibregl.Map | null): void {
  useEffect(() => {
    if (!map) return;
    let cancelled = false;

    const apply = (crops: Crop[]) => {
      if (cancelled) return;
      try {
        buildCropPatternAtlas(map, crops);
      } catch (err) {
        if (import.meta.env.DEV) console.warn('[cropPatternAtlasEffect] build failed', err);
      }
    };

    const run = (crops: Crop[]) => {
      // MapLibre's addImage requires the style to be loaded.
      if (typeof map.isStyleLoaded === 'function' && map.isStyleLoaded()) {
        apply(crops);
      } else {
        const onLoad = () => apply(crops);
        map.once('load', onLoad);
      }
    };

    fetch('/api/v1/reference/crops?limit=500', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() as Promise<CropsListResponse> : Promise.reject(new Error(`crops list ${r.status}`))))
      .then((body) => {
        const crops: Crop[] = (body.items ?? []).map((row) => ({
          id: row.id,
          name: row.name || row.name_translations?.ru || row.name_translations?.en || row.id,
          color: row.color || MAP_NO_DATA_GREY,
          icon_url: row.icon_url ?? null,
        }));
        run(crops);
      })
      .catch((err) => {
        if (import.meta.env.DEV) console.warn('[cropPatternAtlasEffect] fetch crops failed', err);
        // Continue without patterns — planned/harvested polygons will render
        // transparent (filtered by current_phase, no fill-color). Fallback
        // visible через `crop-fill-growing` solid layer для записей с phase
        // unset (matches `!has('current_phase')`).
      });

    return () => {
      cancelled = true;
    };
  }, [map]);
}
